<template>
    <div id="gas-price"
         class="gas-price">
        <!-- Network selector -->
        <div class="gas-price__selector">
            <div v-for="(item, index) in gasPrices"
                 :key="item.network"
                 class="gas-price__selector-item">
                <button class="gas-price__selector-btn"
                        :class="{ 'gas-price__selector-btn--active': index === selectedNetworkIndex }"
                        @click="handleButtonClick(index)">
                    {{ item.network.toUpperCase() }}
                </button>
            </div>
        </div>

        <!-- Gas values -->
        <div class="gas-price__values">
            <a v-for="type in ['slow', 'standard', 'fast']"
               :key="type"
               :href="`${globalChartsRoute}#gas-prices`"
               class="gas-price__value"
               :class="{ 'gas-price__value--bordered': type !== 'fast' }">
                <div class="gas-price__label">
                    <translate :translation-key="`header.${type}`" />
                </div>
                <div class="gas-price__number">
                    {{ gasPrices[selectedNetworkIndex][type] }}
                    <translate translation-key="header.gwei" />
                </div>
            </a>
        </div>

        <!-- Powered by -->
        <div class="gas-price__footer">
            <div class="gas-price__powered-by">
                <translate translation-key="header.poweredBy"
                           fallback="Powered by" />
                &nbsp;{{ dataSource[selectedNetworkIndex] }}
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { ref } from 'vue';

    interface GasPrice {
        network: string;
        slow: number;
        standard: number;
        fast: number;
    }

    defineProps<{
        gasPrices: GasPrice[];
        globalChartsRoute: string;
    }>();

    const selectedNetworkIndex = ref(0);
    const dataSource = ['Etherscan', 'Etherscan', 'Etherscan'];

    const handleButtonClick = (index: number) => {
        selectedNetworkIndex.value = index;
    };
</script>

<style scoped lang="scss">
@use "@/sass/partials/variables" as legacyVariables;
@use "@/sass/design-system/variables" as variables;

.gas-price {
    color: variables.$baseBlack;

    &__selector {
        display: flex;
        padding: 3px;
        border: 1px solid legacyVariables.$purple;
        border-radius: 14px;
        background-color: legacyVariables.$white;
        gap: 4px;

        &-item {
            flex: 1;
        }

        &-btn {
            width: 100%;
            padding: 3px 5px;
            border: none;
            border-radius: 11px;
            background-color: legacyVariables.$white;
            color: legacyVariables.$purple;
            cursor: pointer;
            font-weight: 600;
            text-align: center;
            transition: background 0.2s, color 0.2s;

            &:hover {
                background: legacyVariables.$purple;
                color: variables.$baseWhite;
            }

            &--active {
                background: legacyVariables.$purple;
                color: variables.$baseWhite;
            }
        }
    }

    &__values {
        display: flex;
        min-width: 290px;
        justify-content: center;
        margin-top: 8px;
        text-align: center;

        & > .gas-price__value {
            flex: 1;
            padding: 8px 0;
            text-decoration: none;
            transition: opacity 0.2s;

            &:hover {
                opacity: 0.7;
            }

            &--bordered {
                border-right: 1px solid #dee2e6;
            }
        }
    }

    &__label,
    &__powered-by {
        color: legacyVariables.$name-pill;
        font-size: 13px;
        font-weight: 600;
    }

    &__number {
        color: variables.$baseBlack !important;
        font-size: 14px;
        font-weight: 700;
    }

    &__powered-by {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px 8px;
        border-radius: 6px;
        margin: 12px 0;
        background-color: legacyVariables.$light;
        font-size: 10px;
    }
}

.dark-theme {
    .gas-price {
        &__selector {
            background-color: #2f034b;
        }

        &__label,
        &__powered-by {
            color: #d3d3d3 !important;
        }

        &__number {
            color: variables.$baseWhite !important;
        }

        &__powered-by {
            background-color: #2f034b;
        }

        &__selector-btn {
            background-color: #2f034b;
            color: variables.$baseWhite;

            &:hover,
            &--active {
                background-color: legacyVariables.$purple;
                color: variables.$baseWhite;
            }
        }
    }
}
</style>
